import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { CommonQuestions } from "../components/journey/CommonQuestions";
import { CurrentTasks } from "../components/journey/CurrentTasks";
import { JourneyHeader } from "../components/journey/JourneyHeader";
import { JourneyNotAvailable } from "../components/journey/JourneyNotAvailable";
import { JourneyShellSkeleton } from "../components/journey/JourneyShellSkeleton";
import { StepActionButtons } from "../components/journey/StepActionButtons";
import { StepDetail } from "../components/journey/StepDetail";
import { StepHighlights } from "../components/journey/StepHighlights";
import { StepPills } from "../components/journey/StepPills";
import { CustomTaskModal } from "../components/tasks/CustomTaskModal";
import { JourneyTaskDetailSheet } from "../components/track/JourneyTaskDetailSheet/JourneyTaskDetailSheet";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { useChatActions } from "../contexts/chat-actions-context";
import { useJourneyProgress } from "../hooks/useJourneyProgress";
import { useUser } from "../hooks/useUser";
import { useUserType } from "../hooks/useUserType";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import {
	deleteCustomTask,
	getCustomTasks,
	updateCustomTask,
} from "../services/customTaskService";
import {
	type JourneyTask,
	type JourneyType,
	resolveTreatmentType,
} from "../types/journey";
import type { GetReadyCustomTaskInstance } from "../types/task";
import styles from "./Journey.module.css";

// Maps a custom-task type to the TaskCard icon key (mirrors GetReadyPage).
const CUSTOM_TASK_TYPE_ICON: Record<string, string> = {
	questionnaire: "fill",
	monitor: "monitor",
	sign: "review-sign",
	book: "book",
	upload_doc: "upload",
	test: "test",
	notify: "notify",
};

function formatCustomDeadline(
	deadline: string | null | undefined,
	locale: string,
): string {
	if (!deadline) return "";
	const d = new Date(deadline);
	if (Number.isNaN(d.getTime())) return deadline;
	// Deadlines are stored at UTC midnight; format in UTC so non-UTC timezones
	// don't show an off-by-one day (matches Get Ready / Track formatting).
	return new Intl.DateTimeFormat(locale, {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	}).format(d);
}

// Persisted custom task → JourneyTask for the CurrentTasks list. isCustom drives
// the edit/delete affordances and kind-aware completion.
function customTaskToJourneyTask(
	task: GetReadyCustomTaskInstance,
	locale: string,
): JourneyTask {
	const taskType =
		typeof task.meta?.taskType === "string" ? task.meta.taskType : "";
	return {
		id: task.id,
		title: task.title,
		description: task.description ?? "",
		sevenWordIntro: task.description ?? undefined,
		category: "clinical",
		deadline: formatCustomDeadline(task.deadline, locale),
		status: task.status === "completed" ? "completed" : "ongoing",
		source: "patient",
		iconName: CUSTOM_TASK_TYPE_ICON[taskType] ?? "fill",
		isCustom: true,
	};
}

export function Journey() {
	const { t, direction, locale } = useClientTranslations("journey");
	const navigate = useNavigate();
	const { setPrefillQuestion } = useChatActions();
	const { isD2C } = useUserType();

	const { data: user } = useUser();
	// Resolve the active v5 journey from profile selections (shared helper, also
	// used by the chat journey-context hook). Returns undefined when the only
	// selection maps to no supported journey ("Other"/"I don't know yet") so we
	// render JourneyNotAvailable instead of silently defaulting to IVF.
	const treatmentType: JourneyType | undefined = resolveTreatmentType(
		user?.details,
	);

	const queryClient = useQueryClient();
	const { journey, stepStates, completedTaskIds, isLoading, error, mutations } =
		useJourneyProgress(treatmentType, { locale });
	const isError = Boolean(error);

	const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
	const initializedJourneyIdRef = useRef<string | null>(null);
	const [infoTaskId, setInfoTaskId] = useState<string | null>(null);
	// Custom-task modal: null = closed; { editing: null } = create; { editing }
	// = edit mode. Mirrors GetReadyPage so both surfaces share CustomTaskModal.
	const [taskModal, setTaskModal] = useState<{
		editing: GetReadyCustomTaskInstance | null;
	} | null>(null);

	// Persisted, user-created custom tasks — the same source Get Ready reads, so a
	// task created here shows on both surfaces.
	const { data: rawCustomTasks = [] } = useQuery({
		queryKey: ["customTasks"],
		queryFn: getCustomTasks,
		retry: false,
		throwOnError: false,
	});

	useEffect(() => {
		if (!journey) return;
		// Reset selection when the journey itself changes (e.g., IVF → IUI). The
		// previous step id no longer exists in the new step list.
		if (initializedJourneyIdRef.current === journey.id) return;

		// Prefer live server state (post-refresh continuity): the step the user
		// most recently opened wins, then the most recently completed step, then
		// the CDN-marked "current" step, then the first step.
		const liveStepIds = new Set(journey.steps.map((s) => s.id));
		const inProgress = Array.from(stepStates.values())
			.filter((s) => s.status === "in_progress" && liveStepIds.has(s.stepId))
			.sort((a, b) => (b.openedAt ?? "").localeCompare(a.openedAt ?? ""));
		const completed = Array.from(stepStates.values())
			.filter((s) => s.status === "complete" && liveStepIds.has(s.stepId))
			.sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? ""));
		const fromLive = inProgress[0]?.stepId ?? completed[0]?.stepId;
		const fromCdn = journey.steps.find((s) => s.status === "current")?.id;
		const fallback = journey.steps[0]?.id;
		setSelectedStepId(fromLive ?? fromCdn ?? fallback ?? null);
		initializedJourneyIdRef.current = journey.id;
	}, [journey, stepStates]);

	const showSkeleton = isLoading && !journey;
	const currentStepIndex = journey
		? journey.steps.findIndex((s) => {
				const live = stepStates.get(s.id);
				if (live) return live.status === "in_progress";
				return s.status === "current";
			})
		: -1;
	const effectiveSelectedId = selectedStepId ?? journey?.steps[0]?.id ?? "";
	const selectedStep =
		journey?.steps.find((s) => s.id === effectiveSelectedId) ?? null;
	const customTasks = useMemo(
		() => rawCustomTasks.map((task) => customTaskToJourneyTask(task, locale)),
		[rawCustomTasks, locale],
	);

	const mergedTasks: JourneyTask[] = selectedStep
		? [
				...customTasks,
				...selectedStep.tasks.map((task) => ({
					...task,
					status: completedTaskIds.has(task.id)
						? ("completed" as const)
						: task.status,
				})),
			]
		: [...customTasks];

	const infoIndex = mergedTasks.findIndex((task) => task.id === infoTaskId);
	const infoTask = infoIndex >= 0 ? mergedTasks[infoIndex] : null;

	const handleAddClick = useCallback(() => {
		setTaskModal({ editing: null });
	}, []);

	const handleEditTask = useCallback(
		(task: JourneyTask) => {
			const instance = rawCustomTasks.find((rt) => rt.id === task.id);
			if (instance) setTaskModal({ editing: instance });
		},
		[rawCustomTasks],
	);

	const handleDeleteTask = useCallback(
		(task: JourneyTask) => {
			if (!window.confirm(t("task.confirmDelete"))) return;
			deleteCustomTask(task.id)
				.then(() => {
					queryClient.invalidateQueries({ queryKey: ["customTasks"] });
					toast.success(t("task.toast.deleted"));
				})
				.catch(() => {
					toast.error(t("task.toast.deleteError"));
				});
		},
		[queryClient, t],
	);

	const handleTaskSaved = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["customTasks"] });
	}, [queryClient]);

	// CONTEXT D-04, D-20..D-27 — analytics infrastructure (parent owns analytics
	// per D-28 so child components stay reusable / TASK-10 gate intact).
	const { logJourney } = useAnalytics();

	const handleMarkComplete = useCallback(
		(task: JourneyTask) => {
			logJourney("task_mark_complete", {
				taskId: task.id,
				stepId: effectiveSelectedId,
			});
			// Custom tasks are persisted via customTaskService, not the journey
			// progress mutation (which only knows catalog/step task ids).
			if (task.isCustom) {
				updateCustomTask(task.id, { status: "completed" })
					.then(() => {
						queryClient.invalidateQueries({ queryKey: ["customTasks"] });
						toast.success(t("task.toast.completed"));
					})
					.catch(() => {
						toast.error(t("task.toast.completeError"));
					});
				return;
			}
			mutations.completeTask.mutate(
				{ taskId: task.id, progressPercent: 100 },
				{
					onError: () => {
						toast.error(t("task.toast.completeError"));
					},
					onSuccess: () => {
						toast.success(t("task.toast.completed"));
					},
				},
			);
		},
		[logJourney, mutations.completeTask, effectiveSelectedId, t, queryClient],
	);

	const handleMarkIncomplete = useCallback(
		(task: JourneyTask) => {
			logJourney("task_mark_incomplete", {
				taskId: task.id,
				stepId: effectiveSelectedId,
			});
			if (task.isCustom) {
				updateCustomTask(task.id, { status: "in_progress" })
					.then(() => {
						queryClient.invalidateQueries({ queryKey: ["customTasks"] });
						toast.success(t("task.toast.reopened"));
					})
					.catch(() => {
						toast.error(t("task.toast.reopenError"));
					});
				return;
			}
			mutations.reopenTask.mutate(
				{ taskId: task.id },
				{
					onError: () => {
						toast.error(t("task.toast.reopenError"));
					},
					onSuccess: () => {
						toast.success(t("task.toast.reopened"));
					},
				},
			);
		},
		[logJourney, mutations.reopenTask, effectiveSelectedId, t, queryClient],
	);

	// CONTEXT D-20 — page view fires once, after the journey + initial step have
	// resolved. Guarding on `journey`/`effectiveSelectedId` avoids logging an
	// empty stepId on the first render (before useJourneyProgress settles); the
	// ref only flips once we have a real step, so it still fires exactly once.
	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current || !journey || !effectiveSelectedId) return;
		hasTrackedPageView.current = true;
		logJourney("page_viewed", { stepId: effectiveSelectedId });
	}, [logJourney, journey, effectiveSelectedId]);

	// CONTEXT D-21 — wrap step selector to log selection events.
	// Selecting a pill is VIEW-ONLY: it shows that step's data and moves the
	// selection outline. It must NOT open/close steps — pill fill colors reflect
	// genuine progress (UserStepState), not which step is being browsed. Step
	// progress advances through explicit actions / onboarding, never by viewing.
	const handleSelectStep = useCallback(
		(nextStepId: string) => {
			logJourney("step_selected", {
				stepId: nextStepId,
				prevStepId: selectedStepId ?? "",
			});
			setSelectedStepId(nextStepId);
			// Persist the selection as the opened/current step (fresh openedAt) so
			// returning to Journey re-selects it. Journey init prefers the most
			// recently opened in_progress step. Skip a redundant write when the step
			// is already the active selection.
			if (nextStepId !== effectiveSelectedId) {
				mutations.openStep.mutate({ stepId: nextStepId });
			}
		},
		[logJourney, selectedStepId, effectiveSelectedId, mutations.openStep],
	);

	const handleTaskOpen = useCallback(
		(task: JourneyTask) => {
			logJourney("task_opened", {
				taskId: task.id,
				taskType: task.iconName,
				stepId: effectiveSelectedId,
			});
			const opensOwnPage =
				task.iconName === "book" || task.iconName === "upload";
			if (isD2C && !opensOwnPage) {
				setInfoTaskId(task.id);
				return;
			}
			if (task.iconName === "book") {
				navigate("/get-ready/tasks/book-appointment");
				return;
			}
			logJourney("chevron_to_get_ready", {
				taskId: task.id,
				taskKey: task.iconName ?? "unknown",
			});
			navigate(`/get-ready?taskId=${encodeURIComponent(task.id)}`);
		},
		[logJourney, effectiveSelectedId, navigate, isD2C],
	);

	return (
		<>
			<Header />
			<main className={styles.page} dir={direction}>
				{showSkeleton && <JourneyShellSkeleton />}

				{isError && (
					<FullPageErrorState
						titleKey="errors.loadFailed"
						descriptionKey="errors.loadFailedDescription"
						retryKey="errors.retry"
						onRetry={() =>
							queryClient.invalidateQueries({ queryKey: ["journeyProgress"] })
						}
						t={t}
					/>
				)}

				{!showSkeleton && !isError && !journey && <JourneyNotAvailable />}

				{!showSkeleton && !isError && journey && (
					<>
						<JourneyHeader
							journey={journey}
							currentStepIndex={currentStepIndex}
							totalSteps={journey.steps.length}
							hideProgress={isD2C}
						/>

						<StepPills
							steps={journey.steps}
							selectedStepId={effectiveSelectedId}
							onSelectStep={handleSelectStep}
							completedTaskIds={completedTaskIds}
							stepStates={stepStates}
						/>

						{/* Phase 41 — Step Content + Action Buttons (CONT-01..03, ACT-01, ACT-02) */}
						<StepDetail step={selectedStep} />
						<StepHighlights step={selectedStep} />
						<StepActionButtons
							step={selectedStep}
							onAnalyticsEvent={(action) => {
								if (action === "ask_eve") {
									logJourney("ask_eve", {
										source: "page",
										stepId: effectiveSelectedId,
									});
								} else {
									logJourney("nurse_tapped", {
										source: "page",
										stepId: effectiveSelectedId,
									});
								}
							}}
						/>

						{/* Phase 42 — Current Tasks carousel (TASK-01..09) */}
						<CurrentTasks
							tasks={mergedTasks}
							journey={journey}
							step={selectedStep}
							onAddClick={handleAddClick}
							onTaskOpen={handleTaskOpen}
							onFilterChange={(filter) => {
								logJourney("task_toggled", {
									filter,
									stepId: effectiveSelectedId,
								});
							}}
							onAskEveAnalytics={(task) => {
								logJourney("ask_eve", {
									source: "task",
									stepId: effectiveSelectedId,
									taskId: task.id,
								});
							}}
							onMarkComplete={handleMarkComplete}
							onMarkIncomplete={handleMarkIncomplete}
							onEditTask={handleEditTask}
							onDeleteTask={handleDeleteTask}
						/>

						{/* Phase 43 — Common Questions accordion + Ask Eve / Call Digital Nurse buttons (FAQ-01..04, ANLY-01) */}
						<CommonQuestions
							faqs={selectedStep?.faqs ?? []}
							step={selectedStep}
							stepId={effectiveSelectedId}
							onFaqToggle={(faqId, expanded) => {
								logJourney("faq_toggled", {
									faqId,
									expanded,
									stepId: effectiveSelectedId,
								});
							}}
							onAskEve={(prefill) => {
								logJourney("ask_eve", {
									source: "faq",
									stepId: effectiveSelectedId,
								});
								// Default Ask Eve behavior: set prefill + navigate to /chat.
								// StepActionButtons' default is bypassed when onAskEve is supplied,
								// so we re-implement the default here (CONTEXT D-08).
								setPrefillQuestion(prefill);
								navigate("/chat");
							}}
							onCallNurse={() => {
								logJourney("nurse_tapped", {
									source: "faq",
									stepId: effectiveSelectedId,
								});
								// Default Call Digital Nurse behavior: toast (CONTEXT D-09).
								toast.success(t("callDigitalNurse.comingSoon"));
							}}
						/>

						<JourneyTaskDetailSheet
							isOpen={infoTask !== null}
							task={infoTask}
							onClose={() => setInfoTaskId(null)}
							canPrev={infoIndex > 0}
							canNext={infoIndex >= 0 && infoIndex < mergedTasks.length - 1}
							onPrev={() =>
								setInfoTaskId(mergedTasks[infoIndex - 1]?.id ?? null)
							}
							onNext={() =>
								setInfoTaskId(mergedTasks[infoIndex + 1]?.id ?? null)
							}
							onMarkComplete={() => {
								if (infoTask) handleMarkComplete(infoTask);
								setInfoTaskId(null);
							}}
							markCompleteLabel={t("task.actions.markComplete")}
						/>

						<CustomTaskModal
							isOpen={taskModal !== null}
							editingTask={taskModal?.editing ?? null}
							onClose={() => setTaskModal(null)}
							onSaved={handleTaskSaved}
						/>
					</>
				)}
			</main>
		</>
	);
}
